import { useState, useEffect } from 'react';
import { updateMetaTags } from '../../utils/seo';

export default function ConditionsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    updateMetaTags({
      title: 'Conditions d\'utilisation - Vist Magazine',
      description: 'Conditions générales d\'utilisation du site Vist Magazine. Consultez les règles et conditions d\'accès à notre contenu.',
      keywords: 'conditions utilisation, cgu, vist magazine, règles, conditions générales',
      canonical: '/conditions'
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-xl md:text-2xl font-bold text-gray-900">Vist</a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Accueil</a>
              <a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Magazine</a>
              <a href="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Blog</a>
              <a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">À propos</a>
              <a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            >
              <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-gray-900`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <a href="/" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Accueil</a>
              <a href="/magazine" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Magazine</a>
              <a href="/blog" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Blog</a>
              <a href="/a-propos" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">À propos</a>
              <a href="/contact" className="block py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">Contact</a>
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8 md:mb-12">Conditions d'utilisation</h1>

        <div className="prose prose-sm md:prose-base max-w-none space-y-8">
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">1. Acceptation des conditions</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              En accédant et en utilisant le site Vist Magazine, vous acceptez d'être lié par les présentes conditions générales d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser ce site.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications prendront effet dès leur publication sur cette page.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">2. Accès au site</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              L'accès au site Vist Magazine est gratuit et ouvert à tous. Nous nous efforçons de maintenir le site accessible 24h/24 et 7j/7, mais nous ne pouvons garantir une disponibilité continue.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Nous nous réservons le droit de suspendre, interrompre ou limiter l'accès au site sans préavis, notamment pour des raisons de maintenance, de mise à jour ou de sécurité.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">3. Utilisation du contenu</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Le contenu publié sur Vist Magazine (articles, images, vidéos, graphiques) est protégé par le droit d'auteur et reste la propriété exclusive de Vist Magazine ou de ses contributeurs.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Vous êtes autorisé à :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li>Consulter et lire le contenu pour votre usage personnel</li>
              <li>Partager les articles via les boutons de partage prévus à cet effet</li>
              <li>Citer de courts extraits avec mention de la source et lien vers l'article original</li>
            </ul>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-3">
              Vous n'êtes pas autorisé à :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li>Reproduire, copier ou redistribuer le contenu sans autorisation écrite</li>
              <li>Modifier, adapter ou créer des œuvres dérivées</li>
              <li>Utiliser le contenu à des fins commerciales</li>
              <li>Supprimer les mentions de copyright ou de propriété intellectuelle</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">4. Comportement des utilisateurs</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              En utilisant ce site, vous vous engagez à :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li>Respecter les lois et réglementations en vigueur</li>
              <li>Ne pas porter atteinte aux droits de tiers</li>
              <li>Ne pas diffuser de contenu illégal, offensant, diffamatoire ou inapproprié</li>
              <li>Ne pas tenter de perturber le fonctionnement du site</li>
              <li>Ne pas utiliser de robots, scripts ou autres moyens automatisés pour accéder au site</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">5. Liens externes</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Notre site peut contenir des liens vers des sites web tiers. Ces liens sont fournis uniquement pour votre commodité.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Nous n'exerçons aucun contrôle sur ces sites et déclinons toute responsabilité quant à leur contenu, leur disponibilité ou leurs pratiques en matière de confidentialité.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">6. Limitation de responsabilité</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Vist Magazine s'efforce de fournir des informations exactes et à jour, mais ne peut garantir l'exactitude, l'exhaustivité ou la pertinence du contenu publié.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              En conséquence, nous déclinons toute responsabilité pour :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm md:text-base text-gray-700">
              <li>Les erreurs, inexactitudes ou omissions dans le contenu</li>
              <li>Les dommages directs ou indirects résultant de l'utilisation du site</li>
              <li>Les interruptions de service ou problèmes techniques</li>
              <li>Les pertes de données ou atteintes à la sécurité</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">7. Données personnelles</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              La collecte et le traitement de vos données personnelles sont régis par notre <a href="/confidentialite" className="text-amber-600 hover:text-amber-700 underline cursor-pointer">Politique de confidentialité</a>. Nous vous invitons à la consulter pour comprendre comment nous protégeons vos informations.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">8. Newsletter</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              En vous inscrivant à notre newsletter, vous acceptez de recevoir nos communications par e-mail. Vous pouvez vous désinscrire à tout moment en cliquant sur le lien de désinscription présent dans chaque e-mail.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">9. Propriété intellectuelle</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Tous les éléments du site (textes, images, logos, graphiques, vidéos, code source) sont protégés par le droit d'auteur, le droit des marques et autres droits de propriété intellectuelle.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Toute utilisation non autorisée de ces éléments constitue une violation de nos droits et peut donner lieu à des poursuites judiciaires.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">10. Droit applicable et juridiction</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-3">
              Les présentes conditions d'utilisation sont régies par le droit français.
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              En cas de litige, et à défaut de résolution amiable, les tribunaux français seront seuls compétents.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">11. Contact</h2>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              Pour toute question concernant ces conditions d'utilisation, contactez-nous via notre <a href="/contact" className="text-amber-600 hover:text-amber-700 underline cursor-pointer">page de contact</a>.
            </p>
          </section>

          <section className="pt-6 border-t border-gray-200">
            <p className="text-xs md:text-sm text-gray-500">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 py-12 md:py-16 mt-12 md:mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Vist</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Magazine digital indépendant consacré à l'analyse des tendances culturelles, sociétales et digitales.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><a href="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Accueil</a></li>
                <li><a href="/magazine" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Magazine</a></li>
                <li><a href="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Blog</a></li>
                <li><a href="/a-propos" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">À propos</a></li>
                <li><a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-4">Légal</h4>
              <ul className="space-y-2">
                <li><a href="/mentions-legales" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Mentions légales</a></li>
                <li><a href="/confidentialite" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Confidentialité</a></li>
                <li><a href="/conditions" className="text-sm text-gray-900 font-medium cursor-pointer">Conditions</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-6 md:pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              © {new Date().getFullYear()} Vist Magazine. Tous droits réservés. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-gray-900 transition-colors">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
